import { useLanguage } from "@/hooks/useLanguage";
import { Badge } from "@/components/ui/badge";
import { XCircle, CheckCircle2 } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";

const WhyChatheroSection = () => {
  const { t, tArray } = useLanguage();
  const { ref, isVisible } = useScrollAnimation();

  const centralItems = tArray('whyChathero.central.items');
  const fragmentedItems = tArray('whyChathero.fragmented.items');
  
  return (
    <section 
      ref={ref as React.RefObject<HTMLElement>}
      className={cn(
        "py-16 md:py-24 bg-secondary/50 transition-all duration-700 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">
              {t('whyChathero.tag')}
            </Badge>
            <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-3">
              {t('whyChathero.title')}
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              {t('whyChathero.titleLine2')}
            </p>
          </div>

          {/* Two-Column Grid: Problem vs. Solution */}
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {/* LEFT: Problem Box */}
            <div 
              className={cn(
                "bg-background rounded-2xl p-8 md:p-10 border border-border transition-all duration-500",
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
              )}
              style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
            >
              <h3 className="text-lg font-semibold text-primary mb-6 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-chathero-orange/10 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-chathero-orange" />
                </span>
                {t('whyChathero.fragmented.title')}
              </h3>
              <ul className="space-y-5">
                {fragmentedItems.map((item, i) => (
                  <li 
                    key={i}
                    className={cn(
                      "flex items-start gap-3 transition-all",
                      isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
                    )}
                    style={{ 
                      transitionDelay: isVisible ? `${300 + i * 100}ms` : "0ms",
                      transitionDuration: "400ms"
                    }}
                  >
                    <span className="w-2 h-2 rounded-full bg-chathero-orange/40 flex-shrink-0 mt-2" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="border-t border-border pt-4 mt-6">
                <p className="font-semibold text-foreground">
                  {t('whyChathero.fragmented.conclusion')}
                </p>
              </div>
            </div>

            {/* RIGHT: Solution Box */}
            <div 
              className={cn(
                "bg-white rounded-2xl p-8 md:p-10 border border-chathero-mint/30 shadow-[0_4px_24px_rgba(0,196,180,0.08)] transition-all duration-500",
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
              )}
              style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
            >
              <h3 className="text-lg font-semibold text-primary mb-6 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-chathero-mint/10 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-chathero-mint" />
                </span>
                {t('whyChathero.central.title')}
              </h3>
              <ul className="space-y-5">
                {centralItems.map((item, i) => (
                  <li 
                    key={i}
                    className={cn(
                      "flex items-start gap-3 transition-all",
                      isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"
                    )}
                    style={{ 
                      transitionDelay: isVisible ? `${300 + i * 100}ms` : "0ms",
                      transitionDuration: "400ms"
                    }}
                  >
                    <span className="w-2 h-2 rounded-full bg-chathero-mint/40 flex-shrink-0 mt-2" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="border-t border-chathero-mint/30 pt-4 mt-6">
                <p className="font-semibold text-foreground">
                  {t('whyChathero.central.conclusion')}
                </p>
              </div>
            </div>
          </div>

          {/* Highlight / Abbinder */}
          <div 
            className={cn(
              "bg-chathero-mint/10 border-l-4 border-chathero-mint rounded-r-xl p-6 md:p-8 transition-all duration-500",
              isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
            )}
            style={{ transitionDelay: isVisible ? "600ms" : "0ms" }}
          >
            <p className="text-xl md:text-2xl font-semibold text-primary">
              {t('whyChathero.sectionConclusion')}
            </p>
          </div>

        </div>
      </div>
    </section>
  );
};

export default WhyChatheroSection;
